import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export function AdminConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  requireText = '',
  noteLabel,
  notePlaceholder,
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');

  if (!open) return null;

  const requiresTypedMatch = Boolean(requireText);
  const canConfirm = !requiresTypedMatch || typed.trim() === requireText;
  const toneClasses = tone === 'success'
    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
    : tone === 'warning'
      ? 'bg-amber-500 hover:bg-amber-600 text-white'
      : 'bg-red-500 hover:bg-red-600 text-white';

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm?.(note);
    setTyped('');
    setNote('');
  };

  const handleCancel = () => {
    setTyped('');
    setNote('');
    onCancel?.();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl border border-dark-border bg-dark-card shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-dark-border p-5">
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 rounded-lg p-2 ${tone === 'success' ? 'bg-emerald-500/15 text-emerald-300' : tone === 'warning' ? 'bg-amber-500/15 text-amber-300' : 'bg-red-500/15 text-red-300'}`}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-100">{title}</h3>
              {message && <p className="mt-1 text-sm text-gray-400 leading-relaxed">{message}</p>}
            </div>
          </div>
          <button onClick={handleCancel} className="rounded-lg p-1 text-gray-500 hover:bg-white/5 hover:text-gray-200">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {noteLabel && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">{noteLabel}</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={notePlaceholder}
                rows={3}
                className="w-full resize-none rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-red-500/40"
              />
            </div>
          )}

          {requiresTypedMatch && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Type {requireText} to continue</label>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="w-full rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-red-500/40"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-dark-border p-5 sm:flex-row sm:justify-end">
          <button onClick={handleCancel} className="rounded-lg border border-dark-border bg-dark-surface px-4 py-2 text-sm font-medium text-gray-300 hover:text-white">
            {cancelLabel}
          </button>
          <button disabled={!canConfirm} onClick={handleConfirm} className={`rounded-lg px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${toneClasses}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminPageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-xl font-bold text-gray-100">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function AdminEmptyState({ icon: Icon, title, description }) {
  return (
    <div className="py-12 text-center text-gray-500">
      {Icon && <Icon size={40} className="mx-auto mb-3 opacity-30" />}
      <p className="text-sm text-gray-400">{title}</p>
      {description && <p className="mt-1 text-xs text-gray-600">{description}</p>}
    </div>
  );
}